/**
 * Fuji's PAGODA — the Chureito — in the shared 120×120 LINE asset box.
 *
 * The Home tab and all four passcode dots draw the pagoda. The chat wallpaper
 * draws the MOUNTAIN, which is unusual for this family: almost every other theme
 * here puts the hero on the wallpaper too, or a piece of it. Fuji has two subjects
 * and the reason it can is that they are the two things in its own icon, standing
 * one behind the other — the pagoda in front, the mountain behind. The wallpaper is
 * simply the far one.
 *
 * WHY NOT THE MOUNTAIN ON THE TAB
 *
 * Because this family already has two. Everest is a peak and Etna is a cone, and a
 * third mountain at 22px would be a third triangle — the exact failure the theme
 * next to it in this run (Reynisfjara) avoided by NOT drawing its basalt columns.
 * A pagoda has five roofs and a spire, which nothing else in the set has, and at
 * tab size that stack of horizontals is what carries it.
 *
 * WHY NOT SAKURA'S BLOSSOM ANYWHERE
 *
 * Fuji's icon has cherry trees all across its foreground and they are deliberately
 * absent from every asset here. Sakura is a theme in this same set and the blossom
 * is its whole identity; borrowing it would make two of the twenty-two read as
 * versions of each other.
 *
 * WHAT IS LOAD-BEARING
 *
 *  - FIVE ROOFS, stepping in as they rise. Three reads as a shrine gate and seven
 *    turns the silhouette into a comb.
 *  - THE EAVES FLICK UP at their tips. Two units of upturn, and they are the whole
 *    difference between a pagoda and a stack of hats.
 *  - THE ROOFS OVERHANG far past the bodies they sit on. A pagoda seen small is
 *    mostly roof; drawn with the bodies as wide as the eaves it becomes a tower.
 *  - THE FINIAL IS TALL AND THIN. It is the only vertical in a drawing made of
 *    horizontals, and it is what stops the stack reading as a bookshelf.
 *
 * Every subpath is wound clockwise so a nonzero fill unions them
 * ([[line-shared-glyph-modules]]).
 */

const f = ( v: number ) => +v.toFixed( 1 );

/** A polygon in clockwise order (y down), whichever order it arrives in. */
function cw( pts: [ number, number ][] ): string {
  let area = 0;
  for ( let i = 0; i < pts.length; i++ ) {
    const a = pts[ i ], b = pts[ ( i + 1 ) % pts.length ];
    area += a[ 0 ] * b[ 1 ] - b[ 0 ] * a[ 1 ];
  }
  const p = area < 0 ? pts.slice().reverse() : pts;
  return 'M' + p.map( ( q ) => `${ f( q[ 0 ] ) },${ f( q[ 1 ] ) }` ).join( ' L' ) + ' Z';
}

/* ---------------------------------------------------------------- geometry -- */

const CX = 60;
/** Where the pagoda stands, and how many storeys it has. */
const FOOT = 112, TIERS = 5;
/** The height of one storey, and the widest roof. */
const STOREY = 17, ROOF_W = 42;

/** The roof half-width of tier `i`, counted from the bottom. */
const roofW = ( i: number ): number => ROOF_W - i * 5.4;
/** The eaves height of tier `i`. */
const eaveY = ( i: number ): number => FOOT - 6 - i * STOREY;

/**
 * One ROOF: a shallow wide sweep whose tips flick UP.
 *
 * The upturn is two units and it is the whole difference between a pagoda and a
 * stack of hats. Everything else about the shape can be simplified at tab size;
 * this cannot.
 */
function roof( i: number ): string {
  const w = roofW( i ), y = eaveY( i );
  return `M${ f( CX - w ) },${ f( y ) }`
    + ` L${ f( CX - w + 3 ) },${ f( y - 2.6 ) }`               // the upturned tip
    + ` Q${ f( CX - w * 0.42 ) },${ f( y - 6 ) } ${ CX },${ f( y - 9 ) }`
    + ` Q${ f( CX + w * 0.42 ) },${ f( y - 6 ) } ${ f( CX + w - 3 ) },${ f( y - 2.6 ) }`
    + ` L${ f( CX + w ) },${ f( y ) }`
    // The UNDERSIDE runs nearly flat, with the faintest lift at the middle. Bowed
    // DOWN — the first pass — each roof became a lens, and five lenses stacked up
    // read as a fir tree rather than as a building. A real eave is hollow
    // underneath, so anything but a downward bulge is closer to right than that
    // was.
    + ` Q${ CX },${ f( y - 1.2 ) } ${ f( CX - w ) },${ f( y ) } Z`;
}

/** One BODY: the storey under a roof, much narrower than the eaves above it. */
function body( i: number ): string {
  // NARROWER than the first pass, which took 13 off the eaves and left the bottom
  // storey a 58-unit block under a 84-unit roof. A pagoda seen small is mostly
  // roof; the storeys are what the roofs are resting on and very little else.
  const w = roofW( i ) - 17;
  const top = eaveY( i );
  const bot = i === 0 ? FOOT : eaveY( i - 1 ) - 9;
  return cw( [ [ CX - w, bot ], [ CX - w, top ], [ CX + w, top ], [ CX + w, bot ] ] );
}

export const PAGODA_ROOFS = Array.from( { length: TIERS }, ( _, i ) => roof( i ) ).join( ' ' );
export const PAGODA_BODIES = Array.from( { length: TIERS }, ( _, i ) => body( i ) ).join( ' ' );

/** The platform the whole thing stands on, a little wider than the lowest body. */
export const PAGODA_BASE = cw( [
  [ CX - 30, FOOT + 5 ], [ CX - 30, FOOT ], [ CX + 30, FOOT ], [ CX + 30, FOOT + 5 ],
] );

/**
 * The FINIAL: the spire with its rings, standing on the top roof.
 *
 * The only vertical in a drawing made of horizontals, and what stops the stack
 * reading as a bookshelf.
 */
export const PAGODA_FINIAL = ( () => {
  const top = eaveY( TIERS - 1 ) - 9;
  const parts: string[] = [
    cw( [ [ CX - 1.4, top ], [ CX - 1.4, top - 20 ], [ CX + 1.4, top - 20 ], [ CX + 1.4, top ] ] ),
  ];
  // three rings up the spire — the sorin, and at this size three is what reads
  for ( let k = 0; k < 3; k++ ) {
    const y = top - 6 - k * 5;
    const w = 5 - k * 1.1;
    parts.push( cw( [ [ CX - w, y ], [ CX - w, y - 1.6 ], [ CX + w, y - 1.6 ], [ CX + w, y ] ] ) );
  }
  return parts.join( ' ' );
} )();

/** The whole pagoda as ONE path. This is what the idle tab and the unentered
 *  passcode dot draw. */
export const PAGODA = `${ PAGODA_BASE } ${ PAGODA_BODIES } ${ PAGODA_ROOFS }`
  + ` ${ PAGODA_FINIAL }`;

/**
 * MOUNT FUJI — the wallpaper's grain, and the theme's other subject.
 *
 * A broad cone with a flattened summit and the snowcap breaking down its flanks
 * in tongues. Those tongues are the whole drawing: a straight snow line reads as a
 * paper cutout, and the mountain is famous for exactly the way its snow runs down
 * the gullies.
 */
export const FUJI_MOUNT =
  'M6,96'
  + ' C22,74 40,50 50,42'
  + ' L70,42'
  + ' C80,50 98,74 114,96 Z';

/** Its snowcap, with the tongues. Drawn separately so a two-tone version can put
 *  white on the top and the theme's blue-grey on the rock. */
export const FUJI_SNOW =
  'M50,42 L70,42'
  + ' C74,46 79,53 84,61'
  + ' L79,57 L75,64 L70,56 L66,66 L60,55 L54,66 L50,56 L45,64 L41,57 L36,61'
  + ' C41,53 46,46 50,42 Z';

/**
 * A CLOUD BAR — the wallpaper's second element, and the one place this theme
 * borrows a convention rather than a thing.
 *
 * Japanese landscape prints cut their skies with flat horizontal bands of cloud,
 * and a strip of them is what a print of this mountain looks like more often than
 * not. It is the only second element in this run that is a DEVICE rather than an
 * object, which Madrid's polka dots are also close to being.
 */
export const CLOUD_BAR = ( () => {
  const bar = ( y: number, x0: number, x1: number, h: number ): string =>
    `M${ f( x0 + h ) },${ f( y ) } L${ f( x1 - h ) },${ f( y ) }`
    + ` a${ f( h ) },${ f( h ) } 0 0,1 0,${ f( h * 2 ) }`
    + ` L${ f( x0 + h ) },${ f( y + h * 2 ) }`
    + ` a${ f( h ) },${ f( h ) } 0 0,1 0,${ f( -h * 2 ) } Z`;
  return [ bar( 48, 18, 96, 5 ), bar( 62, 34, 110, 4 ), bar( 74, 12, 78, 4.5 ) ].join( ' ' );
} )();

/* ------------------------------------------------------- passcode colours --
 * Held here rather than in the passcode component: line-lock (the preview the
 * marketplace screenshot shows) and line-page (the exported PNGs) both read
 * them, and every time this family kept two copies of a colour they drifted.
 */

/**
 * The four entered mountains, on the lock screen's blush upper zone (#FFE8E1,
 * sampled from the owner's own shot).
 *
 * They walk ACROSS a dawn rather than down one hue, which is what this theme's
 * palette is: the slate before the sun, the wisteria as it starts, the vermilion at
 * it, then the umber of the earth under all of it. Every other ramp in this family
 * climbs one material; this is the only one that changes subject four times, and it
 * gets away with it because a dawn is a sequence people already know.
 *
 * IT WAS NOT MONOTONIC. Read against the sampled zone the old order measured 3.63 /
 * 2.38 / 3.21 / 10.45 — the second digit quieter than the first, the third quieter
 * than that, and then a jump of three times. A ramp that dips is not a ramp: the
 * eye cannot count it, which is the one job four dots on a lock screen have.
 *
 * Reordering fixed the first three: wisteria is the palest moment of a dawn and
 * belongs first, not second. They run 2.38 / 3.63 / 5.45, each step 1.5x the last.
 *
 * THE FOURTH IS THE OWNER'S CHOICE and it does not climb. They asked for the
 * palette's fifth colour circle — the teal — where a dark umber had been, and the
 * teal measures 2.62 on this blush. So the row reads 2.38 / 3.63 / 5.45 / 2.62.
 *
 * Worth being plain about the trade, because it is a real one: four dots that climb
 * read as a GAUGE filling up, and four that do not read as four colours. This row is
 * now the second kind. It is a defensible thing to want on a theme whose whole
 * palette is a sequence of moments rather than a scale of one material, and it is
 * the only ramp in the family that works this way.
 */
export const PAGODA_PC_COLORS = [ '#BF8AB3', '#5F7D9F', '#A83A17', '#39A1A5' ];

/**
 * ONE tone for every unentered pagoda — the frame never changes per digit (the
 * rule Aurora's orbs settled).
 *
 * 1.44:1 on the blush. Quiet, because on a LIGHT skin the unentered dots are the
 * ones that shout if you let them — the mistake Miami shipped first.
 */
export const PAGODA_PC_OFF = '#E2C7BE';

/**
 * A per-digit lean, alternating side, about the BASE.
 *
 * Small, like Prague's cathedral and Etna's cone: a five-storey timber tower is a
 * plumb-line object, and it is famous for having survived every earthquake in six
 * hundred years without leaning at all.
 */
export const PAGODA_PC_TILTS = [ -4, 3, -2, 5 ];
